import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Truck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useListCarrierAddOnServices,
  useCreateCarrierAddOnService,
  type CreateCarrierAddOnServiceInput,
} from "@/api/carrier-add-on-services";
import { useGetCarriers, type Carrier } from "@/api/carriers";
import type { CarrierAddOnService } from "@/api.types.v2";
import {
  useCarrierScope,
  useCenterScopeNotApplicable,
} from "@/contexts/carrier-scope-context";

// Resolve a providerCarrierId to a display name, falling back to the raw id.
function carrierName(carriers: Carrier[] | undefined, id: string): string {
  return carriers?.find((c) => c.id === id)?.name ?? id;
}

export function PickupServicesScreen() {
  // Add-on services are provider-carrier-scoped (providerCarrierId), not
  // center-scoped — the service catalog sits at the carrier level. Disable
  // the center picker.
  useCenterScopeNotApplicable();
  // Add-on services endpoint takes a single `providerCarrierId`. The screen
  // already groups by carrier (Tabs below), so the first selected carrier is a
  // narrowing hint.
  //
  // ⚠️ This comment used to claim that an empty scope "keeps the catalog showing
  // nothing instead of silently widening". It does the opposite. `undefined` means
  // the param is simply not appended (see useListCarrierAddOnServices), so the
  // endpoint returns EVERY carrier's services — the exact silent widening the
  // comment said it prevented. That is the app-wide behaviour, and it is why the
  // top-bar filter now says "All carriers · no filter" when nothing is selected
  // instead of the old, false "No carriers — showing nothing".
  const { carrierIds: scopedCarrierIds } = useCarrierScope();
  const scopedCarrierId = scopedCarrierIds[0];
  const {
    data: services,
    isLoading,
    isError,
    refetch,
  } = useListCarrierAddOnServices({
    type: "PICKUP",
    providerCarrierId: scopedCarrierId,
  });
  const { data: carriersResp, isLoading: carriersLoading } = useGetCarriers();
  const carriers = carriersResp?.data;

  const [createOpen, setCreateOpen] = useState(false);

  // Group services by provider carrier. Carrier ids that own at least one
  // pickup service become the tabs — sorted by carrier name for stability.
  const groups = useMemo(() => {
    const byCarrier = new Map<string, CarrierAddOnService[]>();
    for (const svc of services ?? []) {
      const list = byCarrier.get(svc.providerCarrierId) ?? [];
      list.push(svc);
      byCarrier.set(svc.providerCarrierId, list);
    }
    return [...byCarrier.entries()]
      .map(([carrierId, svcs]) => ({
        carrierId,
        name: carrierName(carriers, carrierId),
        services: svcs,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [services, carriers]);

  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  // Default the tab to the first group once data lands.
  const currentTab = activeTab ?? groups[0]?.carrierId;
  const loading = isLoading || carriersLoading;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Pickup Services</h1>
          <p className="text-gray-600 mt-2 max-w-2xl">
            Pickup service tiers, grouped by the provider carrier that operates
            them. Codes are lowercase snake_case and permanent (unique per
            carrier) — retire a service by toggling it inactive.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          disabled={!carriers || carriers.length === 0}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add pickup service
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {isError
              ? "Pickup services"
              : loading
                ? "Loading…"
                : `${services?.length ?? 0} service${
                    services?.length === 1 ? "" : "s"
                  } across ${groups.length} carrier${
                    groups.length === 1 ? "" : "s"
                  }`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <div className="text-center p-8 text-muted-foreground">
              <AlertTriangle className="mx-auto h-12 w-12 mb-2 text-destructive opacity-70" />
              <p>Couldn&apos;t load pickup services.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            </div>
          ) : loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              <Truck className="mx-auto h-12 w-12 mb-2 opacity-50" />
              <p>No pickup services yet.</p>
              <p className="text-xs mt-1">
                Add one to a provider carrier to make it available in pickup
                booking flows and the pricing workspace.
              </p>
            </div>
          ) : (
            <Tabs value={currentTab} onValueChange={setActiveTab}>
              <TabsList className="flex-wrap h-auto">
                {groups.map((g) => (
                  <TabsTrigger key={g.carrierId} value={g.carrierId}>
                    {g.name}
                    <Badge
                      variant="secondary"
                      className="ml-2 bg-zinc-100 text-zinc-700"
                    >
                      {g.services.length}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>
              {groups.map((g) => (
                <TabsContent key={g.carrierId} value={g.carrierId}>
                  <CarrierServiceTable services={g.services} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </CardContent>
      </Card>

      <CreatePickupServiceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        carriers={carriers ?? []}
        defaultCarrierId={currentTab}
      />
    </div>
  );
}

function CarrierServiceTable({
  services,
}: {
  services: CarrierAddOnService[];
}) {
  const navigate = useNavigate();

  if (services.length === 0) {
    // Empty-carrier state — a carrier group with zero pickup services.
    return (
      <div className="text-center p-8 text-muted-foreground">
        <Truck className="mx-auto h-10 w-10 mb-2 opacity-50" />
        <p>No pickup services for this carrier yet.</p>
        <p className="text-xs mt-1">
          Use “Add pickup service” to create one for this carrier.
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Code</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">DIM (cm³)</TableHead>
          <TableHead className="text-right">DIM (in³)</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {services.map((svc) => {
          const href = `/add-on-services/services/${svc.id}`;
          return (
            <TableRow
              key={svc.id}
              role="button"
              tabIndex={0}
              aria-label={`Open ${svc.name}`}
              className="hover:bg-muted/50 cursor-pointer focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              onClick={() => navigate(href)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void navigate(href);
                }
              }}
            >
              <TableCell className="font-mono text-sm">{svc.code}</TableCell>
              <TableCell>{svc.name}</TableCell>
              <TableCell>
                {svc.active ? (
                  <Badge
                    variant="secondary"
                    className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                  >
                    Active
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
                  >
                    Inactive
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {svc.pickupDetails?.dimFactorCm ?? (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {svc.pickupDetails?.dimFactorInch ?? (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function CreatePickupServiceDialog({
  open,
  onOpenChange,
  carriers,
  defaultCarrierId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carriers: Carrier[];
  defaultCarrierId?: string;
}) {
  const create = useCreateCarrierAddOnService();
  const [providerCarrierId, setProviderCarrierId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [dimFactorCm, setDimFactorCm] = useState("");
  const [dimFactorInch, setDimFactorInch] = useState("");

  const reset = () => {
    setProviderCarrierId("");
    setCode("");
    setName("");
    setDimFactorCm("");
    setDimFactorInch("");
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = () => {
    const carrierId = providerCarrierId || defaultCarrierId || "";
    if (!carrierId) {
      toast.error("Pick a provider carrier.");
      return;
    }

    const payload: CreateCarrierAddOnServiceInput = {
      providerCarrierId: carrierId,
      type: "PICKUP",
      code: code.trim().toLowerCase(),
      name: name.trim(),
    };
    if (dimFactorCm.trim()) {
      const n = Number(dimFactorCm);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("DIM (cm³) must be a positive number.");
        return;
      }
      payload.dimFactorCm = n;
    }
    if (dimFactorInch.trim()) {
      const n = Number(dimFactorInch);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("DIM (in³) must be a positive number.");
        return;
      }
      payload.dimFactorInch = n;
    }
    if (!payload.code || !payload.name) {
      toast.error("Code and name are required.");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(payload.code)) {
      toast.error(
        "Code must be lowercase letters, digits, and underscores only."
      );
      return;
    }

    create.mutate(payload, {
      onSuccess: () => {
        toast.success(`Pickup service "${payload.name}" created.`);
        reset();
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        const e = err as {
          response?: { status?: number; data?: { error?: string } };
        };
        if (e.response?.status === 409) {
          toast.error(
            "A pickup service with this code already exists for this carrier."
          );
        } else {
          toast.error(e.response?.data?.error ?? "Failed to create service.");
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New pickup service</DialogTitle>
          <DialogDescription>
            Pick the provider carrier that operates this service. Code is
            permanent and unique per carrier — pick something stable like{" "}
            <code>standard</code> or <code>express_am</code>. Lowercase
            snake_case only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="ps-carrier">Provider carrier</Label>
            {/* Native <select>: a Radix Select portal nested inside this Radix
                Dialog recurses in the focus-scope under jsdom, so the picker
                stays a plain accessible control. */}
            <select
              id="ps-carrier"
              value={providerCarrierId}
              onChange={(e) => setProviderCarrierId(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="" disabled>
                Select a carrier
              </option>
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              The carrier that physically operates this pickup tier.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ps-code">Code</Label>
            <Input
              id="ps-code"
              value={code}
              onChange={(e) =>
                // Normalize as the user types so they see what'll actually be
                // submitted — matches the carrier-service create dialog so the
                // confusing "looks uppercase but submits as lowercase" path is
                // gone.
                setCode(
                  e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_")
                )
              }
              placeholder="standard"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, digits, and underscores only. Other characters
              are auto-replaced with underscores.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ps-name">Name</Label>
            <Input
              id="ps-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standard Pickup"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ps-dim-cm">DIM (cm³)</Label>
              <Input
                id="ps-dim-cm"
                type="number"
                step="any"
                value={dimFactorCm}
                onChange={(e) => setDimFactorCm(e.target.value)}
                placeholder="5000"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ps-dim-in">DIM (in³)</Label>
              <Input
                id="ps-dim-in"
                type="number"
                step="any"
                value={dimFactorInch}
                onChange={(e) => setDimFactorInch(e.target.value)}
                placeholder="166"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            L × W × H ÷ DIM factor = dim weight. Leave blank to skip dimensional
            pricing for this service.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
